import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";

import { TransitionEffect } from "~/common/components";
import DatePicker from "~/common/components/ui/DatePicker";
import {
  EXPECTED_DELIVERY_DATE_QUESTION_TITLE,
  getTodayDate,
  INTAKE_MULTI_CHECK_VALUES,
  LAST_MENSTRUAL_CYCLE_QUESTION_TITLE,
  replaceUnderscores,
} from "~/common/utils";
import {
  CompletedRadioWithDateAnswer,
  RadioQuestionWithDateData,
  RadioWithDateAnswer,
} from "~/common/utils/consultations/intake/interfaces";
import { ConsultationQuestionsLayout } from "~/patients/components";
import RadioButtonOption from "~/patients/components/ui/RadioButtonOption";
import {
  radioWithDateAnswerSchema,
  RadioWithDateAnswerType,
} from "~/patients/utils/consultations/intakes";

export interface RadioWithDateFormProps {
  question: RadioQuestionWithDateData;
  handleGoBack: () => void;
  onSubmit: (answer: RadioWithDateAnswer) => void;
  isSubmitting: boolean;
  disableBack: boolean;
  completedAnswer: CompletedRadioWithDateAnswer;
}

export const RadioWithDateForm = ({
  question,
  handleGoBack,
  onSubmit,
  isSubmitting,
  disableBack,
  completedAnswer,
}: RadioWithDateFormProps) => {
  const { options, title } = question;
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
    clearErrors,
  } = useForm<RadioWithDateAnswerType>({
    defaultValues: {
      answer: {
        ...completedAnswer,
      },
    },
    resolver: zodResolver(radioWithDateAnswerSchema),
  });

  const saveAnswers: SubmitHandler<RadioWithDateAnswerType> = (data) => {
    onSubmit(data);
  };

  const valueSelected = watch("answer.value");
  const optionSelected = options.find(({ value }) => value === valueSelected);

  useEffect(() => {
    if (optionSelected?.value === INTAKE_MULTI_CHECK_VALUES.no) {
      reset({
        answer: { value: INTAKE_MULTI_CHECK_VALUES.no },
      });
    }
    if (!errors.answer?.date) return;
    clearErrors("answer.date");
  }, [optionSelected]);

  const today = getTodayDate();
  const hasMaxRestriction =
    optionSelected?.labelAsksExtraInfo === LAST_MENSTRUAL_CYCLE_QUESTION_TITLE;
  const hasMinRestriction =
    optionSelected?.labelAsksExtraInfo ===
    EXPECTED_DELIVERY_DATE_QUESTION_TITLE;

  return (
    <ConsultationQuestionsLayout
      handleNext={handleSubmit(saveAnswers)}
      handleBack={handleGoBack}
      disableNext={!valueSelected}
      errors={errors.answer?.message}
      isSubmitting={isSubmitting}
      title={title}
      disableBack={disableBack}
    >
      {options.map(({ value }) => (
        <RadioButtonOption
          key={value}
          label={replaceUnderscores(value)}
          value={value}
          isSelected={valueSelected === value}
          {...register("answer.value")}
        />
      ))}
      {optionSelected?.asksExtraInfo && (
        <TransitionEffect className="mt-3 flex flex-col gap-4">
          <p className="text-lg font-medium leading-5 first-letter:capitalize">
            {optionSelected.labelAsksExtraInfo}
          </p>
          <DatePicker
            id="date"
            {...register("answer.date")}
            max={hasMaxRestriction ? today : undefined}
            min={hasMinRestriction ? today : undefined}
            error={errors?.answer?.date?.message}
          />
        </TransitionEffect>
      )}
    </ConsultationQuestionsLayout>
  );
};
